/**
 * Immigration programs as data: what a firm needs for each kind of application, per person,
 * with where it comes from. Shared by the app (checklists, deadlines) and the API.
 *
 * Nothing here is legal advice. Every program says which IRCC pages it follows and on what date,
 * and stays "pending" until a lawyer has reviewed it; the app shows both.
 */

export type ProgramFamily = 'family' | 'economic' | 'temporary' | 'citizenship' | 'humanitarian' | 'refugee' | 'appeals';

/** Where a file is. The same stages for every program, so the board reads the same way. */
export type StageId =
  | 'opening' | 'collecting' | 'review' | 'submitted' | 'waiting' | 'additional' | 'decision' | 'closing' | 'closed';

export const STAGES: { id: StageId; label: string }[] = [
  { id: 'opening', label: 'Opening' },
  { id: 'collecting', label: 'Collecting documents' },
  { id: 'review', label: 'Review' },
  { id: 'submitted', label: 'Submitted' },
  { id: 'waiting', label: 'Waiting on IRCC' },
  { id: 'additional', label: 'Additional documents' },
  { id: 'decision', label: 'Decision' },
  { id: 'closing', label: 'Closing' },
  { id: 'closed', label: 'Closed' }
];

export interface ISource {
  title: string;
  url: string;
  /** When we last read it (YYYY-MM-DD). */
  retrieved: string;
}

/** A question asked when the application is opened; the answers decide which items apply. */
export interface IQuestion {
  id: string;
  text: string;
  kind: 'yesno' | 'choice';
  choices?: string[];
}

export type Answers = { [questionId: string]: string | boolean | undefined };

/** Who can be on an application, and how many of them. */
export interface IRole {
  id: string;
  label: string;
  min: number;
  max: number;
}

/** A condition on the answers: {q, is}, or all / any / not of others. */
export type Condition =
  | { q: string; is: string | boolean }
  | { all: Condition[] }
  | { any: Condition[] }
  | { not: Condition };

export interface IChecklistItem {
  /** Stable within the program: checklist rows keep it when a program's version changes. */
  id: string;
  label: string;
  section: string;
  /** Which people it's for: one row per person in these roles; 'application' for one row for the file. */
  forRoles: string[] | 'application';
  /** Only for people of at least this age (e.g. forms signed by everyone 18 or older). */
  minAge?: number;
  when?: Condition;
  /** The IRCC form, when it's a form. */
  form?: string;
  hint?: string;
  /** Documents not in English or French need a certified translation too. */
  translationIfNotEnFr?: boolean;
  /** Index into the program's sources. */
  source: number;
}

/**
 * Things that happen to a file, entered by a person (or later read from a letter). The ids are stored
 * in the firm's lists, so they never change: 'refusalInCanada' is now any refusal that can go to the
 * Federal Court (staff tick when the matter arose outside Canada), and 'refusalOutsideCanada' is kept
 * only so rows recorded before that change still read properly.
 */
export type EventKind =
  | 'aor' | 'biometricsLetter' | 'medicalInstructions' | 'documentRequest' | 'fairnessLetter' | 'interview'
  | 'ita' | 'decisionApproved' | 'refusalInCanada' | 'refusalOutsideCanada' | 'sponsorshipRefusal'
  | 'rpdReferral' | 'hearingDate' | 'radReasons' | 'prraNotice' | 'passportRequest' | 'residencyRefusal'
  | 'residencyRemovalOrder' | 'leaveFiled' | 'fcReasons' | 'permitExpiry' | 'statusLost' | 'programCompleted';

export const EVENT_LABELS: { [k in EventKind]: string } = {
  aor: 'Acknowledgement of receipt',
  biometricsLetter: 'Biometrics instruction letter',
  medicalInstructions: 'Medical exam instructions',
  documentRequest: 'Request for documents',
  fairnessLetter: 'Procedural fairness letter',
  interview: 'Interview notice',
  ita: 'Invitation to apply (Express Entry)',
  decisionApproved: 'Approved by IRCC',
  refusalInCanada: 'Refusal (leave to the Federal Court)',
  refusalOutsideCanada: 'Refusal (decided outside Canada, recorded before October 2026)',
  sponsorshipRefusal: 'Sponsorship refusal',
  rpdReferral: 'Referral to the Refugee Protection Division',
  hearingDate: 'Hearing date',
  radReasons: 'Written reasons from the RPD',
  prraNotice: 'Pre-removal risk assessment notice',
  passportRequest: 'Passport request',
  residencyRemovalOrder: 'Removal order made in Canada (port of entry or inland)',
  residencyRefusal: 'Residency decision made outside Canada (officer abroad)',
  leaveFiled: 'Application for leave filed (Federal Court)',
  fcReasons: 'Reasons received after the Federal Court asked for them',
  permitExpiry: 'Permit or status expires',
  statusLost: 'Status expired without an application to extend it',
  programCompleted: 'Confirmation of program completion (final marks or school letter)'
};

/**
 * Decisions and reasons whose periods run from when they were received. The screen asks for both
 * the date on the letter and the date received, and counts from the earlier of the two.
 */
export const RECEIVED_EVENTS: EventKind[] = [
  'refusalInCanada', 'refusalOutsideCanada', 'sponsorshipRefusal', 'residencyRefusal', 'residencyRemovalOrder', 'radReasons', 'fcReasons'
];

/** Events entered ahead of time (a hearing, a permit's expiry): their date can be in the future. */
export const FUTURE_EVENTS: EventKind[] = ['hearingDate', 'permitExpiry'];

/** Kept only to read older rows; not offered when recording something new. */
export const RETIRED_EVENTS: EventKind[] = ['refusalOutsideCanada'];

/**
 * A deadline that follows an event: so many calendar days after (or before, if negative) the
 * event's date, or "as stated in the letter" when the letter sets it. Always shown as calculated,
 * for a person to check and confirm.
 */
export interface IDeadlineRule {
  id: string;
  event: EventKind;
  label: string;
  days?: number;
  /** The letter sets the date (e.g. procedural fairness letters, 7 to 30 days): a person enters it. */
  asStated?: { min: number; max: number };
  /**
   * Letters that usually state a due date (biometrics, medicals, document requests): the screen asks
   * for that date first, and counts `days` only when the letter has none, saying so.
   */
  letterFirst?: boolean;
  /**
   * A longer period that applies only in some cases. The shorter `days` is always the default; staff
   * use this one only by ticking `tick`, and the choice is recorded with the deadline.
   */
  alt?: { tick: string; days: number; citation: string; caveat?: string };
  citation: string;
  caveat?: string;
  review: 'pending' | { by: string; on: string };
}

export interface IProgram {
  id: string;
  name: string;
  /** A few words, for folder names and lists ("Spousal sponsorship"). */
  short: string;
  family: ProgramFamily;
  /** 'paused' programs can't be opened (e.g. Group of Five intake, paused to Dec 31, 2026). */
  status: 'active' | 'paused';
  pausedNote?: string;
  /** Refugee and private-sponsorship files are restricted to a group of staff. */
  restricted?: boolean;
  /**
   * Temporary residence in Canada: the file has an "Expires on" date, and applying before it keeps
   * the person's status while IRCC decides (maintained status).
   */
  maintainedStatus?: boolean;
  /** Date of the IRCC pages this version follows (YYYY-MM-DD). */
  version: string;
  review: 'pending' | { by: string; on: string };
  sources: ISource[];
  questions: IQuestion[];
  roles: IRole[];
  sections: string[];
  items: IChecklistItem[];
  deadlines: IDeadlineRule[];
}
